/**
 * ApiClient — wrapper fetch com Bearer token, base URL configurável e
 * tratamento uniforme de erro.
 *
 * Base URL: env VITE_API_BASE_URL (default "/v1").
 * Token: injetado via setToken(). Limpo em clearToken().
 *
 * Ponto de extensão: ao integrar com o azim-api real, basta configurar
 * VITE_API_BASE_URL e garantir que o AuthProvider chame setToken/clearToken
 * após o login/logout do Firebase.
 */

import { readErrorBody, normalizeApiError } from "./apiErrorEnvelope.js";
import { RefreshTemporarilyUnavailableError } from "../adapters/auth/AuthPort.js";
import { generateCorrelationId } from "../adapters/http/correlation.js";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    /** Id de correlação do servidor, quando enviado — o elo com o log do backend. */
    public readonly correlationId?: string,
    /**
     * Detalhe técnico do `detail` do ProblemDetails. Fica AQUI e não em `message`
     * quando o status é 5xx: nome de constraint e SQLSTATE não vão para a tela,
     * mas o suporte precisa deles.
     */
    public readonly detail?: string,
    /** Campo do formulário que originou o erro, quando o servidor o identifica. */
    public readonly field?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Converte uma resposta não-ok no `ApiError` correspondente. Ponto ÚNICO de leitura
 * do envelope — a duplicação anterior é o que permitiu as duas cópias divergirem,
 * com o portal inteiro usando a desatualizada (LDG-0125).
 */
async function toApiError(response: Response): Promise<ApiError> {
  const body = await readErrorBody(response);
  const { code, message, correlationId, detail, field } = normalizeApiError(response.status, body);
  return new ApiError(response.status, code, message, correlationId, detail, field);
}

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | null | undefined>;
}

let _token: string | null = null;

/**
 * Provedor de renovação de token (refresh). Quando definido, é chamado uma única
 * vez ao receber 401, para rotacionar o access token e reenviar a requisição.
 * Tipicamente ligado a `AzimAuthAdapter.getIdToken(true)` no bootstrap (main.tsx).
 */
let _tokenRefresher: (() => Promise<string | null>) | null = null;

export function setToken(token: string | null): void {
  _token = token;
}

export function clearToken(): void {
  _token = null;
}

/**
 * Registra (ou remove, com `null`) o provedor de refresh-on-401. O `ApiClient`
 * é um singleton de módulo; o wiring com o adapter de auth acontece no bootstrap.
 */
export function setTokenRefresher(fn: (() => Promise<string | null>) | null): void {
  _tokenRefresher = fn;
}

/**
 * Avisado quando um 401 SOBREVIVE ao refresh-on-401 — ou seja, quando a sessão acabou de verdade
 * e não há renovação possível.
 */
let _onSessionExpired: (() => void) | null = null;

/**
 * Registra (ou remove, com `null`) o aviso de sessão morta. Ligado no bootstrap ao
 * `AuthProvider`, que marca `sessionState = "expired"` — estado que o `RequireAuth` já trata
 * mandando o usuário para o login.
 *
 * <b>Por que isto existe.</b> A máquina de estados do `AuthProvider` documenta
 * `authenticated → expired → authenticating` e o guard trata `expired` desde sempre, mas NADA no
 * aplicativo atribuía esse estado: ele era inalcançável. Sem o aviso, as três saídas do
 * refresh-on-401 terminavam em `throw` e o usuário ficava numa sessão zumbi — a interface
 * mostrando o nome e o papel dele, toda chamada devolvendo 401, e cada tela exibindo o seu erro
 * genérico. Medido em produção: uma rajada de 401 em `/v1/auth/me`, `/v1/inbox`,
 * `/v1/business-units`, `/v1/digest/config` e mais, com a tela oferecendo um "Tentar novamente"
 * que nunca poderia funcionar, porque o problema não era a rota.
 */
export function setSessionExpiredHandler(fn: (() => void) | null): void {
  _onSessionExpired = fn;
}

/**
 * Chama o refresher tolerando indisponibilidade TRANSIENTE.
 *
 * Devolve `{ token }` quando renovou, `{ token: null }` quando a sessão acabou de verdade, e
 * `{ transiente: true }` quando o servidor de autenticação está fora do ar ou devolveu 5xx.
 *
 * A distinção é o ponto: o portal só renova em REAÇÃO ao 401, e o access token vive 15 minutos.
 * Sem separar as duas coisas, a primeira falha transitória depois de qualquer intervalo de
 * inatividade expulsava o usuário — e, como o refresh token é single-use, sem volta.
 */
/**
 * Renovação em voo compartilhada (coalescing) entre TODOS os chamadores de `renovarToken` —
 * refresh-on-401 e refresh-on-403. Sem isto, um lote de chamadas que toma 401/403 ao mesmo
 * tempo (várias telas, várias queries) disparava um `_tokenRefresher()` por chamada; como o
 * refresher de produção (`AzimAuthAdapter.getIdToken(true)`) já teria seu próprio coalescing,
 * o efeito aqui era só desperdício em teste/isolamento — mas em qualquer refresher SEM
 * coalescing próprio (ex.: um futuro adapter mais simples) seria uma tempestade de renovações.
 * Dedup aqui, uma vez, é o que garante a garantia "uma única renovação" independente de quem
 * implementa o refresher.
 */
let _renovacaoEmVoo: Promise<{ token: string | null; transiente: boolean }> | null = null;

async function renovarToken(): Promise<{ token: string | null; transiente: boolean }> {
  if (!_tokenRefresher) return { token: null, transiente: false };
  if (_renovacaoEmVoo) return _renovacaoEmVoo;

  const refresher = _tokenRefresher;
  _renovacaoEmVoo = (async (): Promise<{ token: string | null; transiente: boolean }> => {
    try {
      return { token: await refresher(), transiente: false };
    } catch (erro) {
      // Só o erro TIPADO conta como transiente. Qualquer outra exceção do refresher é defeito, e
      // tratá-la como "tente de novo mais tarde" esconderia o defeito exatamente como o catch nu
      // do adapter escondia — que foi o que tornou o diagnóstico de 03/09 caro.
      if (erro instanceof RefreshTemporarilyUnavailableError) {
        return { token: null, transiente: true };
      }
      throw erro;
    }
  })();

  try {
    return await _renovacaoEmVoo;
  } finally {
    _renovacaoEmVoo = null;
  }
}

declare global {
  interface ImportMeta {
    readonly env: Record<string, string | undefined>;
  }
}

const BASE_URL = (import.meta.env["VITE_API_BASE_URL"]) ?? "/v1";

/**
 * Slug do tenant (header X-Tenant-Slug) exigido pelo authentication service
 * (ex.: GET /v1/auth/me, TenantResolutionMiddleware). Os demais serviços derivam
 * o tenant do claim do JWT e ignoram este header — enviá-lo é inócuo e centraliza
 * a resolução de tenant no gateway. Default "azim" (bootstrap), configurável por env.
 *
 * Exportada porque o `authRepo` também precisa do slug: o POST /v1/auth/password-reset é
 * anônimo e não passa por este cliente, mas o middleware do authentication exige o mesmo
 * header. Uma função, e não duas leituras de env espalhadas, é o que impede os dois caminhos
 * de divergirem — que foi exatamente o defeito que levou o portal a mandar o pedido de
 * redefinição sem slug e o servidor a responder 500 AUTH-ERR-090.
 */
export function resolveTenantSlug(): string {
  return (import.meta.env["VITE_AUTH_TENANT_SLUG"]) ?? "azim";
}

function buildUrl(path: string, params?: Record<string, string | number | boolean | null | undefined>): string {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== null && v !== undefined) {
        url.searchParams.set(k, String(v));
      }
    });
  }
  return url.toString();
}

/**
 * Executa a requisição (headers/token/url) e devolve a `Response` crua,
 * lançando `ApiError` em resposta não-ok. O parsing do corpo fica a cargo do
 * chamador, permitindo tratar corpo vazio (204/201 REST) e ler headers (Location).
 */
async function rawRequest(path: string, options: RequestOptions = {}): Promise<Response> {
  const { params, headers: extraHeaders, ...rest } = options;
  // exactOptionalPropertyTypes: remover params se undefined
  const resolvedParams = params ?? undefined;
  const url = buildUrl(path, resolvedParams);

  // Gerado UMA vez por requisição lógica, fora de `send`: o reenvio após 401 reaproveita o
  // mesmo id, para que a renovação de token não vire duas operações sem relação no log.
  const correlationId = generateCorrelationId();

  /** Dispara o fetch com o token informado (ou anônimo quando null). */
  const send = (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-Tenant-Slug": resolveTenantSlug(),
      "X-Correlation-Id": correlationId,
      ...(extraHeaders as Record<string, string> | undefined),
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return fetch(url, { ...rest, headers });
  };

  const tokenNaTentativaOriginal = _token;
  let response = await send(tokenNaTentativaOriginal);

  // Refresh-on-403: decisão do PO para a virada de autorização por permissão (`permission-based-
  // access`). No deploy em fase única, quem já estava logado carrega um access token SEM o claim
  // `perm` — emitido pela versão anterior do authentication — e os serviços novos negam com 403
  // até esse token vencer (~15min, RefreshToken:AccessTokenLifetime). Renova o token UMA vez e
  // repete a chamada; se a repetição vier 403 de novo, é negação REAL (permissão que o papel de
  // fato não tem) e sobe como está, sem terceira tentativa — nunca entra em loop.
  //
  // Só se aplica quando a tentativa original JÁ era autenticada (havia token). 403 de rota
  // anônima não tem sessão para renovar — e não é o caso que este fluxo existe para resolver.
  //
  // Seguro para mutações (POST/PUT/PATCH/DELETE): 403 de autorização é decidido pelo middleware/
  // guard ANTES do handler rodar — o efeito no backend NUNCA chega a acontecer nesse caminho.
  // Repetir a mutação uma vez não duplica nada; é a MESMA garantia que já vale para o 401.
  if (response.status === 403 && tokenNaTentativaOriginal) {
    const { token: refreshed } = await renovarToken();
    if (refreshed) {
      _token = refreshed;
      response = await send(refreshed);
      // Telemetria discreta — sem PII, só o fato e o path (nenhum dado de negócio).
      console.info("[ApiClient] renovação de token por 403 (claim de permissão desatualizado)", {
        path,
      });
    }
  }

  // Refresh-on-401: ao expirar, renova o token UMA vez e reenvia (RNF 8.3).
  // O refresher (AzimAuthAdapter.getIdToken(true)) bate em /auth/token/refresh,
  // que NÃO passa por este ApiClient — logo não há risco de loop.
  if (response.status === 401) {
    const { token: refreshed, transiente } = await renovarToken();

    // A comparação `refreshed !== _token` que existia aqui matava o reenvio (LDG-0183). O
    // refresher de produção é `AzimAuthAdapter.getIdToken(true)`, e o adapter notifica os
    // observadores AINDA DENTRO da chamada; o AuthProvider é um deles e chama `setToken(...)` na
    // hora. Quando o refresher retornava, `_token` JÁ era o token novo, a guarda era falsa, o
    // reenvio nunca acontecia e TODO 401 caía no aviso de sessão morta — inclusive o benigno de
    // token expirado, que é justamente o caso que o refresh existe para resolver.
    //
    // Sem a guarda, o pior caso é um reenvio inútil quando o refresher devolve um token velho:
    // uma requisição a mais, e o desfecho continua sendo o aviso. Bem melhor do que derrubar
    // sessão viva.
    if (refreshed) {
      _token = refreshed;
      response = await send(refreshed);
    }

    // Se ainda é 401 depois disto, a sessão acabou: ou não havia refresher, ou ele devolveu nulo,
    // ou o reenvio com o token renovado também foi recusado. Nos três casos o `throw` abaixo vira
    // erro de tela — e é isso que produzia a sessão zumbi. O aviso é o que fecha o ciclo até o
    // `expired` do AuthProvider.
    //
    // A exceção é a indisponibilidade transitória: ali a sessão está VIVA e quem falhou foi a
    // renovação. O 401 original sobe como erro da requisição e a sessão fica de pé.
    if (response.status === 401 && !transiente) {
      _onSessionExpired?.();
    }
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  return response;
}

/**
 * Faz o parse tolerante do corpo JSON. Retorna `undefined` quando não há corpo
 * (204, ou 201 REST-padrão com `Content-Length: 0`) — evita o
 * "Unexpected end of JSON input" de `response.json()` em respostas sem corpo.
 */
async function parseJsonBody<T>(response: Response): Promise<T | undefined> {
  if (response.status === 204) return undefined;
  const contentLength = response.headers?.get?.("content-length");
  if (contentLength === "0") return undefined;

  // Caminho real (fetch): lê como texto e tolera vazio.
  if (typeof response.text === "function") {
    const text = await response.text();
    if (!text || text.trim() === "") return undefined;
    return JSON.parse(text) as T;
  }

  // Fallback p/ mocks que só implementam json().
  try {
    return (await response.json()) as T;
  } catch {
    return undefined;
  }
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await rawRequest(path, options);
  return (await parseJsonBody<T>(response)) as T;
}

/**
 * Envia FormData (multipart/form-data) autenticado com Bearer token.
 *
 * Exceção pontual de escopo autorizada pelo enunciado da tarefa: necessária porque
 * platformRepo precisa de upload multipart autenticado mas não tem acesso à variável
 * `_token` (privada de módulo). Esta função reutiliza exatamente o mesmo token,
 * slug de tenant e lógica de refresh-on-401 que `rawRequest`.
 *
 * NÃO define Content-Type: o browser detecta FormData e define automaticamente
 * o header `multipart/form-data; boundary=...` correto. Definir Content-Type
 * manualmente sem o boundary correto quebra o parse de multipart no servidor.
 *
 * @param path   Caminho relativo à BASE_URL (ex.: "/tenant/branding")
 * @param method Método HTTP — "PUT" para atualização, "POST" para criação
 * @param formData FormData com os campos/arquivos a enviar
 */
export async function postMultipart<T>(path: string, method: "PUT" | "POST", formData: FormData): Promise<T> {
  const url = buildUrl(path);
  const correlationId = generateCorrelationId();

  const send = (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {
      "Accept": "application/json",
      "X-Tenant-Slug": resolveTenantSlug(),
      "X-Correlation-Id": correlationId,
      // SEM Content-Type — browser define o boundary do multipart
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return fetch(url, { method, body: formData, headers });
  };

  const tokenNaTentativaOriginal = _token;
  let response = await send(tokenNaTentativaOriginal);

  // Refresh-on-403: mesma lógica de `rawRequest` (ver comentário lá) — uma única renovação e
  // reenvio quando a permissão negada é, na verdade, um token antigo sem o claim `perm`. Upload
  // multipart também é seguro para repetir: o [Authorize]/canDo nega ANTES do handler processar
  // o FormData, então não há efeito parcial no backend para duplicar.
  if (response.status === 403 && tokenNaTentativaOriginal) {
    const { token: refreshed } = await renovarToken();
    if (refreshed) {
      _token = refreshed;
      response = await send(refreshed);
      console.info("[ApiClient] renovação de token por 403 (claim de permissão desatualizado)", {
        path,
      });
    }
  }

  if (response.status === 401) {
    const { token: refreshed, transiente } = await renovarToken();

    // A comparação `refreshed !== _token` que existia aqui matava o reenvio (LDG-0183). O
    // refresher de produção é `AzimAuthAdapter.getIdToken(true)`, e o adapter notifica os
    // observadores AINDA DENTRO da chamada; o AuthProvider é um deles e chama `setToken(...)` na
    // hora. Quando o refresher retornava, `_token` JÁ era o token novo, a guarda era falsa, o
    // reenvio nunca acontecia e TODO 401 caía no aviso de sessão morta — inclusive o benigno de
    // token expirado, que é justamente o caso que o refresh existe para resolver.
    //
    // Sem a guarda, o pior caso é um reenvio inútil quando o refresher devolve um token velho:
    // uma requisição a mais, e o desfecho continua sendo o aviso. Bem melhor do que derrubar
    // sessão viva.
    if (refreshed) {
      _token = refreshed;
      response = await send(refreshed);
    }

    // Se ainda é 401 depois disto, a sessão acabou: ou não havia refresher, ou ele devolveu nulo,
    // ou o reenvio com o token renovado também foi recusado. Nos três casos o `throw` abaixo vira
    // erro de tela — e é isso que produzia a sessão zumbi. O aviso é o que fecha o ciclo até o
    // `expired` do AuthProvider.
    //
    // A exceção é a indisponibilidade transitória: ali a sessão está VIVA e quem falhou foi a
    // renovação. O 401 original sobe como erro da requisição e a sessão fica de pé.
    if (response.status === 401 && !transiente) {
      _onSessionExpired?.();
    }
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  return (await parseJsonBody<T>(response)) as T;
}

export const apiClient = {
  get<T>(path: string, params?: Record<string, string | number | boolean | null | undefined>): Promise<T> {
    return params !== undefined
      ? request<T>(path, { method: "GET", params })
      : request<T>(path, { method: "GET" });
  },
  post<T>(path: string, body: unknown, headers?: Record<string, string>): Promise<T> {
    return request<T>(path, {
      method: "POST",
      body: JSON.stringify(body),
      ...(headers ? { headers } : {}),
    });
  },
  /**
   * POST para criação de recurso (REST 201). Retorna o id do recurso criado,
   * lido do corpo `{ id }` quando presente ou, na ausência de corpo, do último
   * segmento do header `Location` (padrão `CreatedAtAction` do ASP.NET).
   *
   * Lança ApiError(422) quando nem o corpo nem o header Location fornecem um id
   * não-vazio, evitando que callers prossigam com id="" e naveguem para rotas
   * inválidas (ex.: /accounts/'' ou invalidem o cache com id vazio).
   */
  async create(path: string, body: unknown): Promise<{ id: string }> {
    const response = await rawRequest(path, { method: "POST", body: JSON.stringify(body) });

    const parsed = await parseJsonBody<{ id?: string }>(response).catch(() => undefined);
    if (parsed?.id) return { id: parsed.id };

    const location =
      response.headers?.get?.("location") ?? response.headers?.get?.("Location") ?? null;
    const id = location ? (location.split("/").filter(Boolean).pop() ?? "") : "";

    if (!id) {
      throw new ApiError(
        422,
        "MISSING_RESOURCE_ID",
        "O recurso foi criado mas o backend não retornou o id (sem corpo {id} nem header Location acessível).",
      );
    }
    return { id };
  },
  put<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, { method: "PUT", body: JSON.stringify(body) });
  },
  patch<T>(path: string, body: unknown, headers?: Record<string, string>): Promise<T> {
    return request<T>(path, {
      method: "PATCH",
      body: JSON.stringify(body),
      ...(headers ? { headers } : {}),
    });
  },
  delete<T>(path: string): Promise<T> {
    return request<T>(path, { method: "DELETE" });
  },
};
